import {EyeOutlined} from '@ant-design/icons';
import {CheckCircleFilled, ClockCircleOutlined, CloseCircleOutlined} from '@ant-design/icons/lib';
import {Card, Modal, Timeline as _Timeline, Typography} from 'antd';
import {SubscriptionEvent, useSubscriptionEventsQuery} from 'api/generated';
import {AnchorLink} from 'components';
import {Code} from 'components/Code';
import {colors} from 'lib/colors';
import {formatUnixDate} from 'lib/formaters';
import React, {useState} from 'react';
import {useRouteMatch} from 'react-router-dom';
import styled from 'styled-components';
import {useDebouncedCallback} from 'use-debounce';
import {TimelineSubscriptionMessage} from '../../../components/TimelineSubscriptionMessage';
import {getChangedKeys, prettifyEvent} from '../../../lib/utils';
import {EventType} from '../../../types';

const {Text} = Typography;

const Timeline = styled(_Timeline)`
  margin: 10px 0 0 3px !important;
  & .ant-timeline-item-last {
    padding-bottom: 0;
    & > .ant-timeline-item-content {
      min-height: auto;
    }
  }
`;

// Always put subscription.pending first
const eventSorter = (a: any, b: any) => {
  const diff = b.createdAt - a.createdAt;
  if (diff !== 0) return diff;
  return a.type === EventType.SUBSCRIPTION_PENDING ? 1 : -1;
};

export const SubscriptionTimeline: React.FC = () => {
  const match = useRouteMatch<{subscriptionId: string}>();
  const subscriptionId = match.params.subscriptionId;
  const [selectedEvent, setSelectedEvent] = useState<SubscriptionEvent | null>(null);
  const [isInfoOpen, setInfoOpen] = useState(false);
  const {data, loading, refetch} = useSubscriptionEventsQuery({variables: {subscriptionId}});

  const update = useDebouncedCallback(refetch, 5000);

  const events =
    data?.subscriptionEvents.items
      .slice()
      .sort(eventSorter)
      .map((item, i, arr) => ({
        ...item,
        changed: !!arr[i++]
          ? getChangedKeys(item, arr[i++]).map((key) => key.replace(/^object\./, ''))
          : []
      })) || [];

  const handleInfoClose = () => {
    setInfoOpen(false);
  };

  const eventMapping: Record<
    string,
    {title: string | JSX.Element; icon?: React.FC<any>; color?: string; theme?: 'filled'}
  > = {
    [EventType.SUBSCRIPTION_ACTIVATED]: {
      title: 'Subscription activated',
      icon: CheckCircleFilled,
      color: colors.green
    },
    [EventType.SUBSCRIPTION_PENDING]: {
      title: 'Subscription pending',
      icon: ClockCircleOutlined,
      color: colors.secondary
    },
    [EventType.SUBSCRIPTION_CANCELED]: {
      title: 'Subscription canceled',
      icon: CloseCircleOutlined,
      color: colors.red
    },
    [EventType.SUBSCRIPTION_PAUSED]: {
      title: 'Subscription paused',
      icon: ClockCircleOutlined,
      color: colors.secondary
    },
    [EventType.SUBSCRIPTION_UPDATED]: {
      title: 'Subscription updated',
      icon: ClockCircleOutlined,
      color: colors.secondary
    },
    [EventType.SUBSCRIPTION_PAST_DUE]: {
      title: 'Subscription past due',
      icon: ClockCircleOutlined,
      color: colors.secondary
    },
    [EventType.SUBSCRIPTION_TRIALING]: {
      title: 'Subscription trialing',
      icon: ClockCircleOutlined,
      color: colors.secondary
    }
  };

  return (
    <>
      <Card title={'Timeline'} size="small" style={{marginBottom: 20}} loading={loading}>
        <Timeline>
          {events.map((event, i) => {
            const item = eventMapping[event.type];
            return (
              <Timeline.Item
                key={i}
                dot={item.icon ? <item.icon style={{color: item.color}} /> : null}>
                <div>
                  <div>
                    {item.title}{' '}
                    <AnchorLink
                      onClick={() => {
                        setSelectedEvent(event);
                        setInfoOpen(true);
                      }}>
                      <EyeOutlined />
                    </AnchorLink>
                  </div>
                  {event.type === EventType.SUBSCRIPTION_UPDATED && (
                    <TimelineSubscriptionMessage event={event} />
                  )}
                  <Text type="secondary" style={{fontSize: '12px'}}>
                    {formatUnixDate(event.createdAt)}
                  </Text>
                </div>
              </Timeline.Item>
            );
          })}
        </Timeline>
      </Card>
      <Modal
        title={'Event data'}
        width={800}
        open={isInfoOpen}
        footer={false}
        onCancel={handleInfoClose}>
        {selectedEvent && <Code>{prettifyEvent(selectedEvent)}</Code>}
      </Modal>
    </>
  );
};
